import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Payment : famille "mouvement d'argent" constaté entre un tiers et
 * l'organisation (encaissement, acompte, caution restituable, remboursement),
 * rattaché à ce qu'il solde.
 *
 * Modèle "famille + type" (cf. patron asset) : le socle ne connaît pas la
 * nature métier concrète. Le discriminant `type` porte le genre de mouvement
 * déclaré par l'app ("reglement", "acompte", "caution", "remboursement") : c'est
 * ce que la fiche appelle "kind". Les états métier restent des champs à part :
 * `direction` (sens du mouvement) et `method` (moyen de paiement) ne sont PAS le
 * type. Le montant `amount` est le cœur du mouvement, donc requis.
 *
 * Les liens vers les autres familles (contact, order, voucher, credit,
 * subscription, invoice) sont de simples ids string, jamais un import d'un autre
 * schéma. `invoiceIds` est une liste (un mouvement peut solder plusieurs
 * pièces) ; `refundOfPaymentId` pointe le paiement d'origine d'un remboursement.
 *
 * Accès : v0 reste org-wide (même doctrine que asset). L'accès fin par rôle
 * (vente, caisse, facturation) et le portail client (chaque contact ne voit que
 * ses propres pièces) viendront après v0, hors de ce schéma.
 *
 * Données sensibles : on ne stocke JAMAIS le numéro de carte complet, le CVV ni
 * l'IBAN complet (ni en v0 ni après : ils vivent chez le PSP, pas dans
 * Firestore). Seuls le jeton PSP (`pspToken`) et les 4 derniers chiffres
 * (`last4`), non sensibles, sont conservés.
 */

/** Champs métier de la famille payment (hors champs standard du socle). */
export const paymentCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "reglement", "acompte", "caution", "remboursement"). */
  type: z.string().min(1),
  /** Montant du mouvement (positif ; le sens est porté par `direction`). Cœur de la famille, requis. */
  amount: z.number(),
  /** Fiche `contact` du tiers concerné par le mouvement. Optionnel (un encaissement caisse peut être anonyme). */
  contactId: z.string().min(1).optional(),
  /** Moyen de paiement, chaîne libre pilotée par l'app (ex. "card", "cash", "transfer"). Optionnel. */
  method: z.string().min(1).optional(),
  /** Sens du mouvement, chaîne libre pilotée par l'app (ex. "in", "out"). Optionnel. */
  direction: z.string().min(1).optional(),
  /** Date d'encaissement / de constatation effective. Optionnel. */
  paidAt: z.date().optional(),
  /** Factures soldées par ce mouvement (ids). Optionnel. */
  invoiceIds: z.array(z.string()).optional(),
  /** Commande rattachée (id). Optionnel. */
  orderId: z.string().min(1).optional(),
  /** Bon / avoir cadeau rattaché (id). Optionnel. */
  voucherId: z.string().min(1).optional(),
  /** Avoir client rattaché (id). Optionnel. */
  creditId: z.string().min(1).optional(),
  /** Abonnement rattaché (id). Optionnel. */
  subscriptionId: z.string().min(1).optional(),
  /** Paiement d'origine si ce mouvement est un remboursement (id). Optionnel. */
  refundOfPaymentId: z.string().min(1).optional(),
  /** Jeton PSP (tokenisé), non sensible : remplace toute donnée de carte en clair. Optionnel. */
  pspToken: z.string().min(1).optional(),
  /** 4 derniers chiffres du moyen de paiement, pour affichage. Optionnel. */
  last4: z.string().min(1).optional(),
  // Numéro de carte complet, CVV, IBAN complet : sensibles, JAMAIS stockés (ni v0 ni après, ils restent chez le PSP) ; on ne garde que pspToken et last4 ci-dessus.
} as const;

/** Schéma complet de la famille payment (champs standard + champs métier). */
export const PaymentSchema = z.object({
  ...baseEntityFields,
  ...paymentCoreFields,
});

/** Type d'un payment. */
export type Payment = z.infer<typeof PaymentSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const PaymentCreateInputSchema = z.object({
  ...paymentCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un payment. */
export type PaymentCreateInput = z.infer<typeof PaymentCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const PaymentUpdateInputSchema = z
  .object({ ...paymentCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un payment. */
export type PaymentUpdateInput = z.infer<typeof PaymentUpdateInputSchema>;
